/**
 * grade — der ABGELEITETE Umsetzungsgrad und die Aufteilung der 31 Ziele.
 *
 * **Warum es diese Datei gibt.** Atlassian liefert zu keinem der 31 Ziele einen
 * Fortschritt: `progress` ist in `goals.json` durchgehend `null` (geprüft, nicht
 * angenommen). Eine Seite namens „Umsetzungsgrad", die nur Status und Zieldatum
 * zeigt, trüge ihren Namen nicht — also wird der Grad aus UNSEREN eigenen Daten
 * abgeleitet. Genau deshalb steht die Herleitung hier als Funktion und nicht als
 * Zahl in einer Komponente: sie ist prüfbar, benennbar und sie muss sich überall
 * als abgeleitet zu erkennen geben.
 *
 * **Was der Grad misst — und was nicht.**
 *
 *  - `repo:<komponente>` → **Bauzustand** der Komponente (`archModel.buildState`,
 *    dieselbe Quelle, aus der auf der Architekturseite die Form kommt):
 *    in Betrieb = 100 %, im Aufbau = 50 %, Stub/geplant = 0 %.
 *    Extern/fremdbestimmt bleibt bewusst OHNE Zahl — was uns nicht gehört,
 *    bauen wir auch nicht zu einem Prozentsatz.
 *  - `artifact:<typ>` → **Bestand**: Anteil der erfassten Stände, die wirklich
 *    Artefakte führen (`artifacts.json` → `counts`). „0 Artefakte in einem
 *    erfassten Stand" ist eine Aussage und zählt als nicht erfüllt.
 *  - `mechanism:*`, `workstream:*` und Repo-Ziele ohne Komponente → **nichts**.
 *    Für sie gibt es in unseren Daten weder Bauzustand noch Bestand; eine Zahl
 *    wäre hier erfunden. Sie tragen sichtbar „nicht ableitbar".
 *
 * Der Grad ist damit ein Bau-/Bestandsmaß, KEIN Zielfortschritt. Er ersetzt die
 * Zielpflege in Atlassian nicht — er steht in einer eigenen Spalte mit eigener
 * Beschriftung, und die Seite sagt es zusätzlich in einem Satz.
 */
import artifactsData from "@/data/generated/artifacts.json";
import { stateWord } from "@/components/flow";
import { findGoal, GOALS } from "@/components/GoalBadge";
import { buildStateOf } from "@/components/house/archModel";
import { HOUSE_COMPONENTS } from "@/components/house/houseData";
import type { ArtifactIndex, Goal } from "@/types";
import type { FlowState } from "@/types/pipeline";

/**
 * Ein abgeleiteter Grad. `percent === null` heißt „aus unseren Daten nicht
 * ableitbar" — das ist ein Ergebnis, kein fehlender Wert, und wird als solches
 * angezeigt.
 */
export interface Grade {
  percent: number | null;
  /** Woraus abgeleitet — steht als Wort neben der Zahl. */
  basis: "Bauzustand" | "Bestand" | "keine Grundlage";
  /** Der ganze Satz — Tooltip und Vorlesefassung. */
  detail: string;
}

/**
 * Bauzustand → Prozent. Drei Stufen, mehr gibt der Bauzustand nicht her; eine
 * feinere Skala wäre Genauigkeit, die in den Daten nicht steckt.
 * `sealed` (extern) und `ghost` fehlen bewusst → kein Wert.
 */
const BUILD_GRADE: Partial<Record<FlowState, number>> = {
  ok: 100,
  running: 50,
  open: 0,
};

/** Grad einer Komponente aus ihrem Bauzustand. */
export function componentGrade(id: string): Grade {
  const state = buildStateOf(id);
  const word = stateWord(state, "build");
  const percent = BUILD_GRADE[state];
  if (percent === undefined) {
    return {
      percent: null,
      basis: "keine Grundlage",
      detail: `Bauzustand „${word}" — fremdbestimmt, deshalb kein abgeleiteter Grad`,
    };
  }
  return {
    percent,
    basis: "Bauzustand",
    detail: `Bauzustand „${word}" → ${percent} %`,
  };
}

/** Grad eines Artefakt-Typs aus dem Bestand je erfasstem Stand. */
export function artifactTypeGrade(type: string): Grade {
  const counts = (artifactsData as ArtifactIndex).counts?.[type] ?? {};
  const stands = Object.keys(counts);
  if (stands.length === 0) {
    return {
      percent: null,
      basis: "keine Grundlage",
      detail: "kein Stand erfasst — kein abgeleiteter Grad",
    };
  }
  const filled = stands.filter((s) => counts[s] > 0);
  return {
    percent: Math.round((filled.length / stands.length) * 100),
    basis: "Bestand",
    detail: `${filled.length} von ${stands.length} erfassten Ständen führen Artefakte`,
  };
}

/** Für alles, wozu unsere Daten schweigen. Ehrlicher als eine erfundene Zahl. */
export const NOT_DERIVABLE: Grade = {
  percent: null,
  basis: "keine Grundlage",
  detail: "weder Bauzustand noch Bestand bilden dieses Ziel ab — kein abgeleiteter Grad",
};

/** Der abgeleitete Grad zu einem Ziel-`ref` (auch für refs ohne Ziel). */
export function gradeForRef(ref: string): Grade {
  if (ref.startsWith("repo:")) {
    const id = ref.slice("repo:".length);
    return HOUSE_COMPONENTS[id] ? componentGrade(id) : NOT_DERIVABLE;
  }
  if (ref.startsWith("artifact:")) return artifactTypeGrade(ref.slice("artifact:".length));
  return NOT_DERIVABLE;
}

/* ── Aufteilung der Ziele ───────────────────────────────────────────────────
 * Die Architekturübersicht trägt genau die Ziele, deren `ref` auf eine
 * Komponente zeigt. Alles andere braucht einen eigenen Abschnitt — sonst wäre
 * es aus dem Produkt verschwunden, sobald die übrigen Seiten die Ziele
 * verlieren. Die Aufteilung ist deshalb eine RESTMENGEN-Rechnung und keine
 * Aufzählung: was in keinen benannten Abschnitt fällt, landet im Rest-Abschnitt
 * und wird nicht still fallen gelassen. `unreachableGoalRefs()` ist der
 * Nachweis darüber und wird getestet. */

/** Ziel-`ref`s, die die Architekturübersicht selbst zeigt (eine je Komponente). */
export function housedRefs(): Set<string> {
  return new Set(Object.keys(HOUSE_COMPONENTS).map((id) => `repo:${id}`));
}

export interface GoalSectionData {
  id: string;
  title: string;
  hint: string;
  goals: Goal[];
}

const SECTION_DEFS: { id: string; scope: Goal["scope"]; title: string; hint: string }[] = [
  {
    id: "artifact_type",
    scope: "artifact_type",
    title: "Artefakt-Typen",
    hint: "Ziele auf den Extrakten selbst — abgeleitet aus dem Bestand je erfasstem Stand.",
  },
  {
    id: "mechanism",
    scope: "mechanism",
    title: "Mechanismen",
    hint: "Ziele auf Verfahren der Plattform (Runs, Tickets, Artefakt-Inventur) — kein Bau- oder Bestandsmaß.",
  },
  {
    id: "workstream",
    scope: "workstream",
    title: "Workstreams",
    hint: "Ziele über mehrere Komponenten hinweg — kein Bau- oder Bestandsmaß.",
  },
];

/**
 * Die Abschnitte für alle Ziele, die an keiner Komponente hängen — in fester
 * Reihenfolge, leere Abschnitte fallen weg. Der Rest-Abschnitt fängt alles auf,
 * was keiner der benannten `scope`s ist (heute: `repo:maco-roadmap`, ein Repo
 * ohne Eintrag im Kataster).
 */
export function goalSections(): GoalSectionData[] {
  const housed = housedRefs();
  const rest = GOALS.filter((g) => !housed.has(g.ref));
  const sections: GoalSectionData[] = SECTION_DEFS.map((d) => ({
    id: d.id,
    title: d.title,
    hint: d.hint,
    goals: rest.filter((g) => g.scope === d.scope),
  }));
  const claimed = new Set(sections.flatMap((s) => s.goals.map((g) => g.ref)));
  const leftover = rest.filter((g) => !claimed.has(g.ref));
  if (leftover.length > 0) {
    sections.push({
      id: "rest",
      title: "Ohne Komponente",
      hint: "Ziele auf Repos, die (noch) nicht im Kataster stehen — sie hätten sonst keine Stelle in der Anwendung.",
      goals: leftover,
    });
  }
  return sections.filter((s) => s.goals.length > 0);
}

/** Ziele, die WEDER an einer Komponente hängen NOCH in einem Abschnitt stehen. Muss leer sein. */
export function unreachableGoalRefs(): string[] {
  const housed = housedRefs();
  const shown = new Set(goalSections().flatMap((s) => s.goals.map((g) => g.ref)));
  return GOALS.filter((g) => !housed.has(g.ref) && !shown.has(g.ref)).map((g) => g.ref);
}

/* ── Kopfzeile ───────────────────────────────────────────────────────────── */

export interface GoalOverview {
  total: number;
  /** Status → Anzahl, absteigend. */
  byStatus: { status: string; count: number }[];
  /** Nächstes Zieldatum und wie viele Ziele daran hängen. */
  nextDate: string | null;
  nextCount: number;
  /** Ziele mit gepflegtem Atlassian-Fortschritt — heute 0, und das gehört gesagt. */
  withProgress: number;
}

export function goalOverview(): GoalOverview {
  const tally = new Map<string, number>();
  for (const g of GOALS) {
    const s = (g.status as string | null) ?? "pending";
    tally.set(s, (tally.get(s) ?? 0) + 1);
  }
  const dates = GOALS.map((g) => g.target_date).filter((d): d is string => !!d).sort();
  const nextDate = dates[0] ?? null;
  return {
    total: GOALS.length,
    byStatus: [...tally.entries()]
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count || a.status.localeCompare(b.status)),
    nextDate,
    nextCount: nextDate ? dates.filter((d) => d === nextDate).length : 0,
    withProgress: GOALS.filter((g) => g.progress != null).length,
  };
}

/**
 * Der abgeleitete Grad über die ganze Plattform: das Mittel über alle
 * Komponenten mit bewertbarem Bauzustand. Externe Knoten fehlen darin — sie
 * würden das Bild verfälschen, egal in welche Richtung.
 */
export function platformGrade(): { percent: number; counted: number; total: number } {
  const ids = Object.keys(HOUSE_COMPONENTS);
  const values = ids
    .map((id) => componentGrade(id).percent)
    .filter((p): p is number => p != null);
  const percent = values.length === 0 ? 0 : Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  return { percent, counted: values.length, total: ids.length };
}

/* ── Kleinkram ───────────────────────────────────────────────────────────── */

/** ISO-Datum als deutsches Datum; ohne Datum ein Wort statt einer Lücke. */
export function formatGoalDate(iso: string | null | undefined): string {
  if (!iso) return "ohne Zieldatum";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export { findGoal };
